import { Header } from '@innovationnorway/cl-react';
import type {
  AccountMenu,
  LanguageMenu,
  User,
} from '@innovationnorway/cl-react/dist/types/components/header';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { ClusterDto } from 'src/api/v2';
import { StaticIdentifiers } from 'src/constants/cluster-status';
import type { ModuleTypes } from 'src/features/authorization';
import { useAuth, useUser } from 'src/features/authorization';
import { useCluster, useClusters } from 'src/features/cluster';
import { useOneWayInNewOpportunitiesNumber } from 'src/features/one-way-in';
import useStatusPrograms from 'src/hooks/use-status-programs';
import i18n from 'src/i18n';
import { ACCOUNT_SETTINGS_BASE_ROUTE, ROUTES } from 'src/navigation';
import { useLocalStorage } from 'usehooks-ts';
import { AppLanguages, HeaderLink, NavShortcut } from './types';
import useNavItemModules from 'src/hooks/use-nav-item-modules';
import useNavShortcuts from 'src/hooks/use-nav-shortcuts';
import useSiteimprove from 'src/hooks/use-siteimprove';
import useSpaLinkClick from 'src/hooks/use-spa-link-click';
import { SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';
import './Navbar.scss';
import { toast } from 'react-toastify';

const HOME_PATH = '/';

const isMac = /Mac/.test(navigator.userAgent);
const formatShortcut = (key: string) => (isMac ? `⌥⇧${key}` : `Alt+Shift+${key}`);
const withShortcut = (title: string, shortcut?: string) =>
  shortcut ? `${title} (${formatShortcut(shortcut)})` : title;

const Navbar: React.FC = () => {
  const { t: tCommon } = useTranslation();
  const { t: tPageTitles } = useTranslation('pageTitles');
  const { NavItemModules } = useNavItemModules();

  const { cluster } = useCluster();
  const { clusters, storeChosenClusterMutation } = useClusters();
  const { checkAccess } = useAuth();

  const { displayName, isUserLoggedIn, isAadUser, logOut, downloadSwagger } = useUser();
  const { alertOneWayInNewOpportunities } = useOneWayInNewOpportunitiesNumber();
  const { isProgramCluster } = useStatusPrograms();

  const navigate = useNavigate();
  const { log } = useSiteimprove();

  // De store nasjonale aktørene (Forskningsådet, Eksfin osv.) skal ikke behandle henvendelser hos oss, så de trenger
  // ikke å bli distrahert av varsel om et stort antall uleste henvendelser
  const opportunitiesCount = cluster?.oneWayInVmaNames.length === 0 ? alertOneWayInNewOpportunities : 0;

  const navLinks = useMemo<HeaderLink[]>(() => {
    const links: HeaderLink[] = [];

    for (const item of NavItemModules) {
      if (
        // No module check
        item.module === undefined ||
        // If it's an array, make sure we pass at least one
        (Array.isArray(item.module) &&
          item.module.map((module) => checkAccess(module).read).includes(true)) ||
        // If it's only one, make sure we pass
        checkAccess(item.module as ModuleTypes).read === true
      ) {
        // Hvis "SivaCore" og bruker har tilgang til "SivaAdmin", ikke vis "SivaCore"
        if (item.module === 'SivaCore' && checkAccess('SivaAdmin').read === true) {
          continue;
        }

        links.push({
          title: tPageTitles(item.text),
          url: item.path,
          shortcut: item.shortcut,
          count:
            item.path === ROUTES.ONE_WAY_IN.BASE && opportunitiesCount > 0 ? opportunitiesCount : undefined,
        });
      }
    }

    return links;
  }, [NavItemModules, checkAccess, opportunitiesCount, tPageTitles]);

  const menuLinks = useMemo<HeaderLink[]>(() => {
    const links: HeaderLink[] = [];

    if (checkAccess('AccountSettings').read && !isProgramCluster) {
      links.push(
        {
          title: tPageTitles('editMyContactInformation'),
          url: ROUTES.ACTOR_PROFILE.SETTINGS.CONTACT_INFORMATION,
        },
        {
          title: tPageTitles('editAccessControls'),
          url: ROUTES.ACTOR_PROFILE.SETTINGS.ACCESS_CONTROL,
        },
        {
          title: tPageTitles('editMailSettings'),
          url: ROUTES.ACTOR_PROFILE.SETTINGS.EMAIL,
        },
        {
          title: tPageTitles('actorProfile'),
          url: ROUTES.ACTOR_PROFILE.BASE,
          shortcut: 'P',
        },
      );
    }

    if (checkAccess('AccountSettings').read && isProgramCluster) {
      links.push({
        title: tPageTitles('settings'),
        url: ACCOUNT_SETTINGS_BASE_ROUTE,
      });
    }
    if (process.env.NODE_ENV !== 'production') {
      links.push({
        title: 'DEV: Swagger',
        onClick() {
          downloadSwagger();
          toast.success('Lastet ned swagger.json fra ' + process.env.NODE_ENV);
        },
      });
    }

    return links;
  }, [checkAccess, isProgramCluster, tPageTitles, downloadSwagger]);

  const shortcuts = useMemo<NavShortcut[]>(
    () =>
      [...navLinks, ...menuLinks].reduce<NavShortcut[]>((accumulated, link) => {
        if (link.shortcut && link.url) {
          accumulated.push({ key: link.shortcut, path: link.url });
        }

        return accumulated;
      }, []),
    [menuLinks, navLinks],
  );

  useNavShortcuts(shortcuts);

  const user = useMemo<User>(() => {
    const homeLink = navLinks.find((link) => link.url === HOME_PATH);

    // Re-ordering of links so that they are displayed in the order we want
    // Links with shortcuts first, then links without shortcuts
    const combinedLinks = [...navLinks.filter((link) => link.url !== HOME_PATH), ...menuLinks];
    const linksWithShortcut = combinedLinks.filter((link) => !!link.shortcut);
    const linksWithoutShortcut = combinedLinks.filter((link) => !link.shortcut);

    const orderedLinks = [...linksWithShortcut, ...linksWithoutShortcut];

    // Header krever url for "pages", så lenker uten url (kun onClick) vises som actions i stedet
    const pageLinks = orderedLinks.filter((link): link is HeaderLink & { url: string } => !!link.url);
    const actionOnlyLinks = orderedLinks.filter((link) => !link.url);

    return {
      name: displayName || undefined,
      links: {
        homePage: homeLink
          ? { title: withShortcut(homeLink.title, homeLink.shortcut), url: homeLink.url ?? HOME_PATH }
          : undefined,
        pages: pageLinks.map((link) => ({
          title: withShortcut(link.title, link.shortcut),
          url: link.url,
          count: link.count,
          onClick: link.onClick,
        })),
      },
      actions: [
        ...actionOnlyLinks.map((link) => ({
          title: withShortcut(link.title, link.shortcut),
          onClick: link.onClick,
        })),
        { title: tCommon('logOut'), onClick: logOut },
      ],
    };
  }, [displayName, logOut, menuLinks, navLinks, tCommon]);

  const selectClusterHandler = useCallback(
    async (selectedCluster: ClusterDto) => {
      if (selectedCluster.id === cluster?.id) return;

      await storeChosenClusterMutation.mutateAsync(selectedCluster);

      if (!isAadUser) {
        log(
          SITEIMPROVE_DECLARATIONS.CLUSTER_CHOOSER.id,
          SITEIMPROVE_DECLARATIONS.CLUSTER_CHOOSER.actions.selected(selectedCluster.id),
          SITEIMPROVE_DECLARATIONS.CLUSTER_CHOOSER.labels.HEADER,
        );
      }

      navigate(HOME_PATH);
    },
    [cluster?.id, isAadUser, log, navigate, storeChosenClusterMutation],
  );

  const accountMenu = useMemo<AccountMenu>(() => {
    const selectableClusters = clusters
      .filter((item) => item.clusterStatus && item.clusterStatus.id !== StaticIdentifiers.NotSet)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    return {
      accounts: selectableClusters.map((item) => ({
        accountName: item.name || '',
        accountId: item.id,
        isSelected: item.id === cluster?.id,
        onClick: () => selectClusterHandler(item),
      })),
    };
  }, [cluster, clusters, selectClusterHandler]);

  // Språk
  const [selectedLanguage, setSelectedLanguage] = useLocalStorage<AppLanguages>('lang', 'nb');

  const selectLanguageHandler = useCallback(
    (lang: AppLanguages) => {
      switch (lang) {
        case 'nb':
          log(
            SITEIMPROVE_DECLARATIONS.CHANGE_LANGUAGE.id,
            SITEIMPROVE_DECLARATIONS.CHANGE_LANGUAGE.actions.CHANGE_TO_NB,
            displayName,
          );
          break;
        case 'nn':
          log(
            SITEIMPROVE_DECLARATIONS.CHANGE_LANGUAGE.id,
            SITEIMPROVE_DECLARATIONS.CHANGE_LANGUAGE.actions.CHANGE_TO_NN,
            displayName,
          );
          break;
        case 'en':
          log(
            SITEIMPROVE_DECLARATIONS.CHANGE_LANGUAGE.id,
            SITEIMPROVE_DECLARATIONS.CHANGE_LANGUAGE.actions.CHANGE_TO_EN,
            displayName,
          );
          break;
      }
      i18n.changeLanguage(lang);
      setSelectedLanguage(lang);
      document.documentElement.lang = lang;
    },
    [displayName, log, setSelectedLanguage],
  );

  const languages = useMemo<LanguageMenu['languages']>(
    () => [
      {
        locale: 'nb',
        title: 'Bokmål',
        isSelected: selectedLanguage === 'nb',
        onClick: () => selectLanguageHandler('nb'),
      },
      {
        locale: 'nn',
        title: 'Nynorsk',
        isSelected: selectedLanguage === 'nn',
        onClick: () => selectLanguageHandler('nn'),
      },
      {
        locale: 'en',
        title: 'English',
        isSelected: selectedLanguage === 'en',
        onClick: () => selectLanguageHandler('en'),
      },
    ],
    [selectLanguageHandler, selectedLanguage],
  );

  const logNavigation = useCallback(
    (pathname: string) => log(SITEIMPROVE_DECLARATIONS.NAVIGATION.id, pathname),
    [log],
  );
  const handleHeaderClick = useSpaLinkClick(logNavigation);

  if (!isUserLoggedIn) {
    return null;
  }

  return (
    <div onClickCapture={handleHeaderClick}>
      <Header
        id="app-navbar"
        className="app-navbar"
        ariaLabel={tCommon('mainMenu')}
        locale={selectedLanguage}
        logo={{
          name: selectedLanguage === 'en' ? 'in-eng' : 'in',
          href: HOME_PATH,
          label: tCommon('toFrontpage'),
        }}
        user={user}
        accountMenu={accountMenu}
        languageMenu={{ languages }}
      />
    </div>
  );
};

export default Navbar;
